import { useState } from 'react';
import { FaTruckFast, FaBoxOpen, FaMagnifyingGlass, FaCheck, FaCircleExclamation } from "react-icons/fa6";

// Libellés des statuts du suivi DHD Livraison (clés en minuscules, DHD renvoie par ex. "Return_received")
const DHD_STATUS_LABELS = {
    order_information_received_by_carrier: "Commande enregistrée et validée par le vendeur",
    picked: "Commande récupérée par le prestataire de livraison",
    accepted_by_carrier: "Commande réceptionnée par le centre de tri",
    dispatched_to_driver: "Commande dispatchée au livreur",
    attempt_delivery: "Tentative de livraison",
    return_asked: "Retour initié par le centre de tri",
    return_in_transit: "Retour en transit",
    return_received: "Retour réceptionné par le vendeur",
    livred: "Commande livrée",
    encaissed: "Commande encaissée",
    payed: "Paiement effectué",
};

// Convertit les activités du suivi DHD au format de l'historique affiché (états + événements), du plus récent au plus ancien
const buildDhdHistory = (activities) =>
    [...activities]
        // DHD renvoie la date et l'heure en heure d'Algérie (UTC+1, pas d'heure d'été)
        .map((activity) => ({ ...activity, dateObj: new Date(`${activity.date}T${activity.time || "00:00:00"}+01:00`) }))
        .sort((a, b) => b.dateObj - a.dateObj)
        .map((activity, i) => ({
            id: i + 1,
            state: {
                state_name: DHD_STATUS_LABELS[(activity.status || "").toLowerCase()] || activity.status,
                depot_id: null,
            },
            events: [{
                event_description: [activity.reason, activity.station && `Station : ${activity.station}`].filter(Boolean).join(" — "),
                event_date: activity.dateObj,
            }],
            completed: true,
        }));

const Tracking = ({ onContactRequest }) => {

    const optionsDateLong = {weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',hour12: false};
    const optionsDateTime = {timeZone: 'Africa/Algiers',year: 'numeric',month: '2-digit',day: '2-digit',hour: '2-digit',minute: '2-digit',second: '2-digit',hour12: false};
    const optionsDate = {timeZone: 'Africa/Algiers',year: 'numeric',month: '2-digit',day: '2-digit',hour12: false};
    const optionsTime = {timeZone: 'Africa/Algiers',hour: '2-digit',minute: '2-digit',second: '2-digit',hour12: false};

    const [query, setQuery] = useState("");
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const handleSearch = (e) => {
        e.preventDefault();
        if (!query) return;

        setLoading(true);
        setResult(null);
        setError("");

        const url = `https://serverapp.samligne-express.com/api/package/${query}/tracking_client`;
        /* const url = `http://localhost:9095/api/package/${query}/tracking_client`; */
        fetch(url)
            .then(res => res.json())
            .then((result) => {
                if (result["err"]) {
                    if (result["err"]["code"] === "PACKAGE_NOT_FOUND") setError(`Aucune information trouvée pour ce numéro de tracking.`);
                    else setError(`Une erreur est survenue. La recherche n’a pas pu être effectuée.`);
                } else {
                    const package_infos = result?.data?.package || [];
                    const tracking = result?.data?.tracking || [];
                    // Suivi DHD Livraison (renseigné par le serveur si le colis a un package_dhd_tracking)
                    const dhdActivities = result?.data?.dhd_tracking?.activity || [];
                    const useDhdTracking = dhdActivities.length > 0;

                    if (package_infos.length <= 0 || (tracking.length <= 0 && !useDhdTracking)) {
                        setError(`Aucune information trouvée pour ce numéro de tracking.`);
                    } else {
                        const trackingData = {
                            trackingNumber: package_infos[0]["package_tracking_number"] || "",
                            estimatedDelivery: package_infos[0]["package_delivery_delay"] || "",
                            from : `${package_infos[0]["init_depot_wilaya_name"] || ""} - ${package_infos[0]["init_depot_commune_name"] || ""}`,
                            destination : `${package_infos[0]["receiver_client_wilaya_name"] || ""} - ${package_infos[0]["receiver_client_commune_name"] || ""}`,
                            currentStepIndex: 1,
                            // Colis affecté à DHD : afficher le suivi DHD, sinon le suivi existant
                            history: useDhdTracking
                                ? buildDhdHistory(dhdActivities)
                                : tracking.map((item,i) => ({
                                    id: i+1,
                                    state: item,
                                    events : item["events"],
                                    completed: true
                                }))
                        };
                        setResult(trackingData);
                    }
                }
            })
            .catch((error) => {
                console.log(error);
                setError(`Une erreur est survenue (Connexion impossible).`);
            }).finally(() => setLoading(false));
    };

    return (
        <>
            <div className="text-center w-full max-w-2xl animate-in fade-in slide-in-from-bottom-4 duration-700">
                <div className="inline-flex items-center justify-center p-3 bg-brand-50 rounded-full mb-6 ring-1 ring-blue-400/30 backdrop-blur-sm">
                    <FaBoxOpen className="text-brand-500 w-8 h-8" />
                </div>

                <h1 className="text-4xl md:text-7xl font-black text-white mb-6 drop-shadow-lg tracking-tight">
                    Suivez votre colis
                </h1>
                <h6 className="text-slate-300 text-lg md:text-xl mb-10 max-w-lg mx-auto leading-relaxed">
                    Saisissez votre numéro de suivi pour localiser votre commande en temps réel.
                </h6>

                <form onSubmit={handleSearch} className="relative w-full shadow-2xl rounded-2xl overflow-hidden group transition-all hover:scale-[1.01]">
                    <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none text-slate-400 group-focus-within:text-blue-600 transition-colors">
                        <FaMagnifyingGlass className="w-5 h-5" />
                    </div>
                    <input
                        type="text"
                        className="block w-full pl-12 pr-36 py-5 bg-white text-lg text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-4 focus:ring-blue-500/30 transition-all"
                        placeholder="Ex: DZ-192837465"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                    />
                    <button
                        disabled={loading}
                        className="absolute right-2 top-2 bottom-2 bg-blue-600 hover:bg-blue-700 text-white font-bold px-8 rounded-xl transition-all shadow-md disabled:opacity-70 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        {loading ? (
                            <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></span>
                        ) : (
                            <>Suivre <FaTruckFast /></>
                        )}
                    </button>
                </form>

                {error && (
                    <div className="mt-6 flex items-center justify-center gap-2 text-red-200 bg-red-900/50 backdrop-blur-md px-4 py-3 rounded-lg text-sm font-medium border border-red-500/30 animate-in zoom-in-95">
                        <FaCircleExclamation /> {error}
                    </div>
                )}
            </div>

            {result && (
                <div className="w-full max-w-3xl mt-16 bg-white rounded-3xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom-8 fade-in duration-500">

                    {/* Header Result */}
                    <div className="bg-slate-50 p-6 md:p-8 border-slate-100 flex flex-col md:flex-row justify-between md:items-center gap-4">
                        <div>
                            <span className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1">
                                <FaBoxOpen /> N° Suivi
                            </span>
                            <h2 className="text-3xl font-black text-slate-800 tracking-tight mt-1">{result.trackingNumber}</h2>
                        </div>

                        {result.estimatedDelivery ?

                            <div className="text-left md:text-right bg-blue-50 px-4 py-2 rounded-lg border border-blue-100">
                                <span className="text-xs font-bold text-blue-600 uppercase tracking-wider">Estimation livraison</span>
                                <p className="text-lg font-bold text-slate-800">{result.estimatedDelivery} Jour</p>
                            </div>

                            : ""}

                    </div>

                    {/* Route Visuelle */}
                    {/* <div className='package-route bg-slate-50'>
                        <div className="step start">
                            <div className="content">
                                <div className="title"><span className="dot"></span><span>Point de départ</span></div>
                                <div className="subtitle">{result.from}</div>
                            </div>
                        </div>
                        <div className="step end">
                            <div className="content">
                                <div className="title"><span className="dot"></span><span>Point d’arrivée</span></div>
                                <div className="subtitle">{result.destination}</div>
                            </div>
                        </div>
                    </div> */}

                    {/* Timeline History */}
                    <div className="p-6 md:p-10 bg-white">
                        <div className="relative border-l-2 border-slate-100 ml-3 md:ml-4 space-y-10 pb-2">
                            {result.history.map((step) => (
                                <div key={step.id} className="relative pl-8 md:pl-12 group">
                                    <div className={`absolute -left-[11px] top-1 h-6 w-6 rounded-full border-4 flex items-center justify-center bg-white transition-all duration-500 z-10 ${step.completed ? 'border-blue-600 text-blue-600' : 'border-slate-200'}`}>
                                        {step.completed && <div className="w-2.5 h-2.5 bg-blue-600 rounded-full" />}
                                    </div>
                                    <div className="flex flex-col sm:flex-row gap-2 sm:gap-10">
                                        <div className="flex-1">
                                            <div className="flex justify-between items-center">
                                                <h3 className="text-lg font-bold text-slate-700 flex items-center gap-2">
                                                    {step.state.state_name} {step.completed && <FaCheck className="text-green-500 text-sm" />}
                                                </h3>
                                                {

                                                    step.state.depot_id ?
                                                        <div className="text-slate-400 font-bold text-xs leading-relaxed text-start flex items-center gap-1">
                                                            <svg class="w-3.5 h-3.5 text-indigo-500 flex-shrink-0" style={{display:"inline"}} fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clip-rule="evenodd"></path></svg>
                                                            {`${step.state.depot_name} - ${step.state.depot_commune_name} (${step.state.depot_wilaya_name})`}
                                                        </div> : ""

                                                }
                                            </div>
                                            <div>
                                                {step.events.map((event, eventIndex) => (
                                                    <div key={event.event_id ?? eventIndex} className='mt-3 bg-dark'>
                                                        <p className="text-slate-500 font-bold text-sm leading-relaxed text-start">
                                                            {event.event_description || "Changement d'état"}
                                                        </p>
                                                        <div className="flex justify-between items-center gap-2 text-xs">
                                                            <div>
                                                                <span className="text-slate-400 font-medium me-1">
                                                                    {new Date(event.event_date).toLocaleString("fr-FR",optionsDate)}
                                                                </span>
                                                                <span className="text-slate-400 font-medium">
                                                                    {new Date(event.event_date).toLocaleString("fr-FR",optionsTime)}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Footer Carte */}
                    <div className="bg-slate-50 p-4 text-center border-t border-slate-100">
                        <button
                            onClick={() => onContactRequest(result.trackingNumber)}
                            className="text-sm text-blue-600 font-bold hover:text-blue-800 transition-colors flex items-center justify-center gap-2 mx-auto"
                        >
                            <FaCircleExclamation /> Besoin d'aide avec cette commande ?
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};

export default Tracking;
